"use client"

import { useState, useRef, useEffect } from "react"
import { Play, Pause, Volume2, VolumeX } from "lucide-react"
import Image from "next/image"
import { Button } from "@/components/ui/button"

interface HeroVideoProps {
  className?: string
}

export default function HeroVideo({ className }: HeroVideoProps) {
  const [isPlaying, setIsPlaying] = useState(true)
  const [isMuted, setIsMuted] = useState(true)
  const [isLoaded, setIsLoaded] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const youtubeUrl = `https://www.youtube.com/embed/4MYOltTiel8?autoplay=1&mute=${isMuted ? 1 : 0}&controls=0&loop=1&playlist=4MYOltTiel8&modestbranding=1&playsinline=1`


  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
      } else {
        videoRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted
      setIsMuted(!isMuted)
    }
  }

  useEffect(() => {
    const video = videoRef.current
    if (video) {
      const handlePlay = () => setIsPlaying(true)
      const handlePause = () => setIsPlaying(false)

      video.addEventListener("play", handlePlay)
      video.addEventListener("pause", handlePause)

      // Attempt autoplay
      video.play().catch(() => {
        // Autoplay may fail
      })

      return () => {
        video.removeEventListener("play", handlePlay)
        video.removeEventListener("pause", handlePause)
      }
    }
  }, [])

  return (
    <div className={`relative w-full h-full bg-slate-900 rounded-lg overflow-hidden ${className}`}>
      {/* Hero Image Placeholder */}
      <Image
        src="/hero.png"
        alt="Real Metro Metal Hero"
        fill
        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 1920px"
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${isLoaded ? "opacity-0" : "opacity-100"}`}
        priority
        fetchPriority="high"
        quality={65}
      />

      {/* Video */}
      <video
        ref={videoRef}
        className={`w-full h-full object-cover absolute inset-0 transition-opacity duration-700 ${isLoaded ? "opacity-100" : "opacity-0"}`}
        loop
        muted={isMuted}
        autoPlay
        playsInline
        preload="none"
        onCanPlay={() => setIsLoaded(true)}
      >
        <source src="/hero.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>



      {/* Gradient overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
    </div>
  )
}
